import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { IUser } from '../types';
import { authApi } from '../api/endpoints';

interface AuthState {
  user: IUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<boolean>;
  register: (data: { firstName: string; lastName: string; email: string; phone: string; password: string; gender: string; referralCode?: string; role?: string }) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  clearError: () => void;
  setUser: (user: IUser) => void;
  updateUser: (updates: Partial<IUser>) => void;
  handleCredentialResponse: (response: any, referralCode: string) => Promise<Boolean>
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authApi.login(email, password);


          if (response.success && response.data) {
            const { user, token } = response.data;

            // Store token in localStorage for API requests
            localStorage.setItem('auth_token', token);

            set({
              user,
              token,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });



            return true;
          } else {
            set({
              isLoading: false,
              error: response.error || 'Login failed',
            });
            return false;
          }
        } catch (error: any) {
          console.error('Auth store login error:', error);
          set({
            isLoading: false,
            error: error.response?.data?.error || error.message || 'An unexpected error occurred',
          });
          return false;
        }
      },

      register: async (data) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authApi.register(data);
          if (response.success && response.data) {
            const { user, token } = response.data;


            set({
              user,
              token,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });

            // Store token in localStorage for API requests
            localStorage.setItem('auth_token', token);

            return true;
          } else {
            set({
              isLoading: false,
              error: response.error || 'Registration failed',
            });
            return false;
          }
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.message || 'An unexpected error occurred',
          });
          return false;
        }
      },

      logout: async () => {
        set({ isLoading: true });

        try {
          await authApi.logout();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          // Clear state regardless of API response
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });

          // Remove token from localStorage
          localStorage.removeItem('auth_token');
        }
      },

      refreshToken: async () => {
        try {
          const response = await authApi.refreshToken();

          if (response.success && response.data) {
            const { token } = response.data;

            set({ token });
            localStorage.setItem('auth_token', token);

            return true;
          } else {
            // Token refresh failed, logout user
            get().logout();
            return false;
          }
        } catch (error) {
          console.error('Token refresh error:', error);
          get().logout();
          return false;
        }
      },

      clearError: () => {
        set({ error: null });
      },

      setUser: (user: IUser) => {
        set({ user });
      },

      updateUser: (updates: Partial<IUser>) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...updates } });
        }
      },

      handleCredentialResponse: async (credential: string, referralCode?: string): Promise<Boolean> => {
        set({ isLoading: true, error: null });

        try {

          const response = await authApi.googleLogin(credential, referralCode);

          if (response && response.data) {
            const { user, token } = response.data;


            set({
              user,
              token,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });

            // Store token in localStorage for API requests
            localStorage.setItem('auth_token', token);

            return true;
          } else {
            set({
              isLoading: false,
              error: response.error || 'Google Login failed',
            });
            return false;
          }
        } catch (error: any) {
          console.error('Google Auth store login error:', error);
          set({
            isLoading: false,
            error: error.response?.data?.error || error.message || 'An unexpected error occurred',
          });
          return false;
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Restore token to localStorage after rehydration
        if (state?.token) {
          localStorage.setItem('auth_token', state.token);
        }
      },
    }
  )
);

// Selector hooks for optimized re-renders
export const useAuthUser = () => useAuthStore((state) => state.user);
export const useAuthToken = () => useAuthStore((state) => state.token);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);
